import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { CheckCircle, Loader2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import axios from "axios";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_POLLS = 5;
const POLL_INTERVAL_MS = 2000;

const usePaymentStatus = (sessionId, type) => {
  const [status, setStatus] = useState("loading");
  const [paymentData, setPaymentData] = useState(null);
  const pollCount = useRef(0);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return undefined;
    }
    let cancelled = false;
    let timer = null;
    const urls = {
      booking: `${API}/bookings/status/${sessionId}`,
      cart: `${API}/cart/status/${sessionId}`,
    };
    const url = urls[type] || `${API}/checkout/status/${sessionId}`;

    const poll = async () => {
      if (cancelled) return;
      if (pollCount.current >= MAX_POLLS) {
        setStatus("timeout");
        return;
      }
      try {
        const res = await axios.get(url);
        if (cancelled) return;
        setPaymentData(res.data);
        if (res.data.payment_status === "paid") {
          setStatus("success");
          return;
        }
        if (res.data.status === "expired") {
          setStatus("expired");
          return;
        }
        pollCount.current += 1;
        timer = setTimeout(poll, POLL_INTERVAL_MS);
      } catch {
        if (cancelled) return;
        pollCount.current += 1;
        if (pollCount.current >= MAX_POLLS) {
          setStatus("error");
        } else {
          timer = setTimeout(poll, POLL_INTERVAL_MS);
        }
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [sessionId, type]);

  return { status, paymentData };
};

const fmtDate = (iso) =>
  iso
    ? new Date(`${iso}T00:00:00`).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })
    : "";

const LoadingView = () => (
  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
    <Loader2 size={48} className="animate-spin text-primary mx-auto mb-6" />
    <h1 className="font-heading text-3xl font-normal text-foreground mb-4" data-testid="payment-processing">
      Processing Payment...
    </h1>
    <p className="font-body text-base text-muted-foreground">
      Please wait while we confirm your payment.
    </p>
  </motion.div>
);

const BookingSuccessView = ({ paymentData }) => {
  const reservationConfirmed = paymentData?.reservation_status === "confirmed";
  return (
    <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
      <CheckCircle size={56} className="text-green-600 mx-auto mb-6" />
      <h1 className="font-heading text-3xl font-normal text-foreground mb-4" data-testid="booking-success">
        {reservationConfirmed ? "Booking Confirmed!" : "Payment Received!"}
      </h1>
      {paymentData && (
        <div className="rounded-lg bg-muted/50 p-5 mb-6 text-left space-y-2">
          <div className="flex justify-between font-body text-sm">
            <span className="text-muted-foreground">Check-in</span>
            <span className="text-foreground font-medium" data-testid="booking-success-checkin">{fmtDate(paymentData.check_in)}</span>
          </div>
          <div className="flex justify-between font-body text-sm">
            <span className="text-muted-foreground">Check-out</span>
            <span className="text-foreground font-medium" data-testid="booking-success-checkout">{fmtDate(paymentData.check_out)}</span>
          </div>
          <div className="flex justify-between font-body text-sm">
            <span className="text-muted-foreground">Guests</span>
            <span className="text-foreground font-medium">{paymentData.guests_count}</span>
          </div>
          {paymentData.addons?.length > 0 && (
            <>
              <div className="flex justify-between font-body text-sm border-t border-border/60 pt-2">
                <span className="text-muted-foreground">Stay</span>
                <span className="text-foreground font-medium">${paymentData.stay_amount?.toFixed(2)}</span>
              </div>
              {paymentData.addons.map((a) => (
                <div key={a.id} className="flex justify-between font-body text-sm" data-testid={`success-addon-${a.id}`}>
                  <span className="text-muted-foreground">+ {a.name}</span>
                  <span className="text-foreground font-medium">${a.price?.toFixed(2)}</span>
                </div>
              ))}
            </>
          )}
          <div className="flex justify-between font-body text-sm border-t border-border/60 pt-2">
            <span className="text-muted-foreground">Total paid</span>
            <span className="text-foreground font-semibold" data-testid="booking-success-amount">
              ${paymentData.amount?.toFixed(2)} {paymentData.currency}
            </span>
          </div>
        </div>
      )}
      <p className="font-body text-sm text-muted-foreground mb-8" data-testid="booking-success-note">
        {reservationConfirmed
          ? "Your reservation has been created in our booking system. You will receive a confirmation email shortly."
          : "Your payment is complete. Our team is finalizing your reservation and will email you confirmation shortly."}
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link to="/">
          <Button data-testid="payment-home" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
            Return Home
          </Button>
        </Link>
        <Link to="/experiences">
          <Button data-testid="payment-more" variant="outline" className="font-body rounded-full px-6 border-primary text-primary hover:bg-primary hover:text-white">
            Add Experiences to Your Stay
          </Button>
        </Link>
      </div>
    </motion.div>
  );
};

const CartSuccessView = ({ paymentData }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
    <CheckCircle size={56} className="text-green-600 mx-auto mb-6" />
    <h1 className="font-heading text-3xl font-normal text-foreground mb-4" data-testid="cart-success">
      Order Confirmed!
    </h1>
    {paymentData && (
      <div className="rounded-lg bg-muted/50 p-5 mb-6 text-left space-y-2">
        {(paymentData.stays || []).map((s) => (
          <div key={`${s.listing_title}-${s.check_in}`} className="flex justify-between font-body text-sm gap-3" data-testid="cart-success-stay">
            <span className="text-muted-foreground">
              {s.listing_title} · {fmtDate(s.check_in)} → {fmtDate(s.check_out)}
              {s.reservation_status !== "confirmed" && " (finalizing)"}
            </span>
            <span className="text-foreground font-medium shrink-0">${s.amount?.toFixed(2)}</span>
          </div>
        ))}
        {(paymentData.experiences || []).map((e) => (
          <div key={e.id} className="flex justify-between font-body text-sm" data-testid={`cart-success-exp-${e.id}`}>
            <span className="text-muted-foreground">{e.name} × {e.qty}</span>
            <span className="text-foreground font-medium">${e.subtotal?.toFixed(2)}</span>
          </div>
        ))}
        <div className="flex justify-between font-body text-sm border-t border-border/60 pt-2">
          <span className="text-muted-foreground">Total paid</span>
          <span className="text-foreground font-semibold" data-testid="cart-success-amount">
            ${paymentData.amount?.toFixed(2)} {paymentData.currency}
          </span>
        </div>
      </div>
    )}
    <p className="font-body text-sm text-muted-foreground mb-8">
      Thank you for booking with Texoma Resorts. You will receive a confirmation email shortly.
    </p>
    <div className="flex flex-wrap justify-center gap-3">
      <Link to="/">
        <Button data-testid="payment-home" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
          Return Home
        </Button>
      </Link>
    </div>
  </motion.div>
);

const SuccessView = ({ paymentData }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
    <CheckCircle size={56} className="text-green-600 mx-auto mb-6" />
    <h1 className="font-heading text-3xl font-normal text-foreground mb-4" data-testid="payment-success">
      Payment Successful!
    </h1>
    <p className="font-body text-base text-muted-foreground mb-2">
      Thank you for booking with Texoma Resorts.
    </p>
    {paymentData && (
      <p className="font-body text-sm text-muted-foreground mb-8">
        Amount: ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
      </p>
    )}
    <p className="font-body text-sm text-muted-foreground mb-8">
      We will send you a confirmation email with details about your experience.
    </p>
    <div className="flex flex-wrap justify-center gap-3">
      <Link to="/">
        <Button data-testid="payment-home" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
          Return Home
        </Button>
      </Link>
      <Link to="/experiences">
        <Button data-testid="payment-more" variant="outline" className="font-body rounded-full px-6 border-primary text-primary hover:bg-primary hover:text-white">
          Browse More Experiences
        </Button>
      </Link>
    </div>
  </motion.div>
);

const FailureView = ({ status }) => (
  <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }}>
    <XCircle size={56} className="text-destructive mx-auto mb-6" />
    <h1 className="font-heading text-3xl font-normal text-foreground mb-4" data-testid="payment-failed">
      {status === "expired" ? "Session Expired" : "Payment Issue"}
    </h1>
    <p className="font-body text-base text-muted-foreground mb-8">
      {status === "timeout"
        ? "Payment is still being processed. Please check your email for confirmation."
        : "There was an issue with your payment. Please try again or contact us."}
    </p>
    <div className="flex flex-wrap justify-center gap-3">
      <Link to="/experiences">
        <Button data-testid="payment-retry" className="bg-primary hover:bg-primary/90 text-white font-body rounded-full px-6">
          Try Again
        </Button>
      </Link>
      <Link to="/contact">
        <Button variant="outline" className="font-body rounded-full px-6 border-primary text-primary hover:bg-primary hover:text-white">
          Contact Us
        </Button>
      </Link>
    </div>
  </motion.div>
);

export default function PaymentSuccessPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const type = searchParams.get("type");
  const { status, paymentData } = usePaymentStatus(sessionId, type);
  const { clearCart } = useCart();

  useEffect(() => {
    if (type === "cart" && status === "success") clearCart();
  }, [type, status, clearCart]);

  const renderSuccess = () => {
    if (type === "cart") return <CartSuccessView paymentData={paymentData} />;
    if (type === "booking") return <BookingSuccessView paymentData={paymentData} />;
    return <SuccessView paymentData={paymentData} />;
  };

  return (
    <>
      <Helmet>
        <title>Payment Status | Texoma Resorts</title>
        <meta name="robots" content="noindex" />
      </Helmet>

      <section className="pt-28 pb-24 md:pt-32 min-h-[70vh] flex items-center">
        <div className="max-w-lg mx-auto px-6 md:px-12 text-center w-full">
          {status === "loading" && <LoadingView />}
          {status === "success" && renderSuccess()}
          {(status === "error" || status === "expired" || status === "timeout") && (
            <FailureView status={status} />
          )}
        </div>
      </section>
    </>
  );
}
